import { keepPreviousData, queryOptions, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import * as apiClient from "@/lib/api";
import { useAuthorisation } from "@/lib/auth/components/auth-provider";
import { ROLES } from '@/lib/auth/roles'
import { getGuideYear } from "@/lib/filters/date/date-utils";
import { useQueryResetWithRefresh } from '@/lib/hooks/query-refetch-with-reset'
import { TopRatedStatusApiResponse } from "@/lib/types/top-rated-score";
import { FIVE_MINUTES } from "@/utils/constants";
import { QUERY_KEY_TOP_RATED_SCORE_FOR_CURRENT_YEAR } from "@/utils/keys";

const { VITE_QUERY_STALE_TIME } = import.meta.env
const STALE_TIME = VITE_QUERY_STALE_TIME ? Number(VITE_QUERY_STALE_TIME) : FIVE_MINUTES

function fetchTopRatedScoreForCurrentYear(): Promise<TopRatedStatusApiResponse> {
  return apiClient.get(`/firm/top-rated-firm-network/current-year-score?guideYear=${getGuideYear()}`)
}

export const topRatedScoreForCurrentYearQueryOptions = () =>
  queryOptions({
    queryKey: [QUERY_KEY_TOP_RATED_SCORE_FOR_CURRENT_YEAR],
    queryFn: fetchTopRatedScoreForCurrentYear,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

type TopRatedQueryResult = UseQueryResult<TopRatedStatusApiResponse, Error>

function mapQueryResult(query: TopRatedQueryResult, resetAndRefetch: () => void) {
  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    isError: query.isError,
    isRefetchError: query.isRefetchError,
    error: query.error,
    isRefetching: query.isRefetching,
    isSuccess: query.isSuccess,
    status: query.status,
    refetch: resetAndRefetch
  }
}
type TopRatedQueryOptions = ReturnType<typeof topRatedScoreForCurrentYearQueryOptions>

function buildTopRatedHook(queryOptionsFactory: () => TopRatedQueryOptions) {
  return function useTopRated() {
    const options = queryOptionsFactory()
    const query = useQuery(options)
    const { resetAndRefetch } = useQueryResetWithRefresh(options.queryKey, query.refetch)

    return mapQueryResult(query, resetAndRefetch)
  }
}

export const useTopRatedScoreForGuideYear = buildTopRatedHook(topRatedScoreForCurrentYearQueryOptions)

export const useElevationSpecificElements = () => {
  const {verifyRoles} = useAuthorisation()
  const hasElevation = verifyRoles({allowedRoles: [ROLES.ROLE_ELEVATION]})

  const titleColor = hasElevation ? 'text-mint-green' : 'text-grey'
  const className = hasElevation ? '' : '[&_div]:!text-grey'

  return {titleColor, className, hasElevation}
}
